import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useCallback, useMemo, useState } from 'react'
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import {
    ActivityIndicator,
    Dialog,
    HelperText,
    Searchbar,
    Text,
    useTheme,
} from 'react-native-paper'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useDebouncedState } from '@/hooks/useDebouncedState'
import { useStore } from '@/lib/pocketbase'
import { PickerDialog } from '@/ui/PickerDialog'

interface JobPickerProps<T extends FieldValues = Record<string, unknown>> {
    name: Path<T>
    control: Control<T>
    orgSlug: string
    label?: string
    includeAllStatuses?: boolean
    allowClear?: boolean
    placeholder?: string
    testID?: string
}

export function JobPicker<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    orgSlug,
    label = 'Job *',
    includeAllStatuses = false,
    allowClear = false,
    placeholder = 'Select a job',
    testID,
}: JobPickerProps<T>) {
    const [visible, setVisible] = useState(false)
    const dismissDialog = useCallback(() => setVisible(false), [])
    const {
        value: searchInput,
        debounced: debouncedSearch,
        setValue: setSearch,
    } = useDebouncedState('', 300)
    const { org: currentOrg } = useCurrentOrg({
        throwIfNotFound: false,
        onlySlug: orgSlug,
    })
    const [jobsCollection] = useStore('jobs')
    const theme = useTheme()

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    const { value, onChange } = field

    const { isLoading, data: allJobs = [] } = useLiveQuery(
        q =>
            currentOrg?.id
                ? q
                      .from({ jobs: jobsCollection })
                      .where(({ jobs }) =>
                          includeAllStatuses
                              ? eq(jobs.org, currentOrg.id)
                              : and(eq(jobs.org, currentOrg.id), eq(jobs.status, 'ACTIVE'))
                      )
                      .orderBy(({ jobs }) => jobs.name, 'asc')
                : undefined,
        [currentOrg?.id, includeAllStatuses]
    )

    const jobs = useMemo(() => {
        if (!debouncedSearch) return allJobs
        const lowerSearch = debouncedSearch.toLowerCase()
        return allJobs.filter(job => job.name.toLowerCase().includes(lowerSearch))
    }, [allJobs, debouncedSearch])

    const selectedJob = allJobs.find(j => j.id === value)

    return (
        <View style={styles.wrapper} testID={testID}>
            <TouchableOpacity
                onPress={() => setVisible(true)}
                style={[
                    styles.picker,
                    {
                        borderColor: theme.colors.outline,
                        backgroundColor: theme.colors.surface,
                    },
                ]}
            >
                <View style={styles.pickerContent}>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        {label}
                    </Text>
                    <Text
                        variant="bodyLarge"
                        style={{
                            color: value ? theme.colors.onSurface : theme.colors.onSurfaceVariant,
                        }}
                    >
                        {selectedJob?.name || placeholder}
                    </Text>
                </View>
            </TouchableOpacity>
            {error ? <HelperText type="error">{error.message}</HelperText> : null}

            <PickerDialog visible={visible} onDismiss={dismissDialog}>
                <View style={styles.dialogHeader}>
                    <Searchbar
                        placeholder="Search jobs..."
                        value={searchInput}
                        onChangeText={setSearch}
                        style={styles.headerSearchbar}
                    />
                </View>
                <Dialog.ScrollArea>
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="small" />
                        </View>
                    ) : jobs.length === 0 ? (
                        <View style={styles.emptyContainer}>
                            <Text
                                variant="bodyMedium"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                {debouncedSearch ? 'No jobs found' : 'No jobs available'}
                            </Text>
                        </View>
                    ) : (
                        <FlatList
                            data={jobs}
                            keyExtractor={item => item.id}
                            style={styles.list}
                            ListHeaderComponent={
                                allowClear && value ? (
                                    <TouchableOpacity
                                        style={styles.item}
                                        onPress={() => {
                                            onChange('')
                                            setVisible(false)
                                            setSearch('')
                                        }}
                                    >
                                        <Text
                                            variant="bodyLarge"
                                            style={[
                                                styles.itemText,
                                                {
                                                    color: theme.colors.onSurfaceVariant,
                                                    fontStyle: 'italic',
                                                },
                                            ]}
                                        >
                                            Clear selection
                                        </Text>
                                    </TouchableOpacity>
                                ) : null
                            }
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    style={styles.item}
                                    onPress={() => {
                                        onChange(item.id)
                                        setVisible(false)
                                        setSearch('')
                                    }}
                                >
                                    <Text
                                        variant="bodyLarge"
                                        style={[styles.itemText, { color: theme.colors.onSurface }]}
                                    >
                                        {item.name}
                                    </Text>
                                </TouchableOpacity>
                            )}
                        />
                    )}
                </Dialog.ScrollArea>
            </PickerDialog>
        </View>
    )
}

const styles = StyleSheet.create({
    wrapper: {
        marginBottom: 8,
    },
    picker: {
        borderWidth: 1,
        borderRadius: 4,
        padding: 12,
    },
    pickerContent: {
        gap: 4,
    },
    dialogHeader: {
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
    },
    headerSearchbar: {
        elevation: 0,
    },
    list: {
        maxHeight: 300,
    },
    item: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 4,
    },
    itemText: {
        flex: 1,
    },
    loadingContainer: {
        padding: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyContainer: {
        padding: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
})
